import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { SelfServiceTicketDTO } from '@/api/trafikportalen';
import { DialogWindow } from '@/components/Dialog';
import { FormElement } from '@/components/FormElement';
import Button from '@/components/core/Button';
import { ChangeBearerDialog } from '@/components/SelfService/ChangeBearerDialog';
import { TicketActivationDetails } from '@/components/Tickets/TicketActivationDetails';
import {
  COLLECTION_TYPE_LABELS,
  isStudentCollectionType,
} from '@/helpers/collectionTypes';
import { ticketStatusDeadline } from '@/helpers/ticket';
import { useTicketStatusOptions } from '@/hooks/useTicketTranslations';
import { IconEdit } from '@/icons';
import type { BearerListItem } from '@/hooks/useBearerMapping';

interface SelfServiceTicketCardProps {
  ticket: SelfServiceTicketDTO;
  travellerId: string;
  bearerItems: BearerListItem[];
  mostRecentBobcatBearerId?: string;
}

export const SelfServiceTicketCard = ({
  ticket,
  travellerId,
  bearerItems,
  mostRecentBobcatBearerId,
}: SelfServiceTicketCardProps) => {
  const { formatMessage } = useIntl();
  const { getStatusTranslationLong } = useTicketStatusOptions();
  const [changeBearerOpen, setChangeBearerOpen] = useState(false);

  const canChangeBearer =
    !ticket.recipient_name &&
    (ticket.state === 'active' || ticket.state === 'inactive');

  const currentBearer = bearerItems.find(
    (bearer) => bearer.id === ticket.bobcat_mtb_bearer_id,
  );

  const status = getStatusTranslationLong(
    ticket,
    ticketStatusDeadline(ticket, ticket),
  );

  const collectionTypeLabel = useMemo(() => {
    const collectionType = ticket.collection_type;
    if (collectionType && isStudentCollectionType(collectionType)) {
      return formatMessage(COLLECTION_TYPE_LABELS[collectionType]);
    }

    return formatMessage({ defaultMessage: 'Enstaka biljett' });
  }, [formatMessage, ticket.collection_type]);

  const statusContent = useMemo(() => {
    if (
      !status.description ||
      !status.ariaDescription ||
      status.ariaDescription === status.description
    ) {
      return (
        <p className="break-words">
          {status.label}
          {status.description ? ` ${status.description}` : null}
        </p>
      );
    }

    return (
      <p className="break-words">
        {status.label} <span aria-hidden="true">{status.description}</span>
        <span className="sr-only">{status.ariaDescription}</span>
      </p>
    );
  }, [status.ariaDescription, status.description, status.label]);

  const bearerContent = useMemo(() => {
    if (currentBearer?.name) {
      return <span className="truncate">{currentBearer.name}</span>;
    }
    if (currentBearer?.serial) {
      return (
        <span className="font-mono font-light leading-4 opacity-70">
          {currentBearer.serial}
        </span>
      );
    }
    return (
      <i>
        <FormattedMessage defaultMessage="Okänd bärare" />
      </i>
    );
  }, [currentBearer]);

  return (
    <li className="flex flex-col gap-2 rounded-md border border-background-300 p-3">
      <div className="flex flex-col gap-1">
        <p className="font-medium text-text-900">{ticket.product_class}</p>
        {!ticket.loan_transaction_id && (
          <p className="truncate text-text-700">{ticket.department_name}</p>
        )}
      </div>
      <FormElement
        label={formatMessage({ defaultMessage: 'Kategori:' })}
        input={<p className="break-words">{collectionTypeLabel}</p>}
      />
      <FormElement
        label={formatMessage({ defaultMessage: 'Status:' })}
        input={statusContent}
      />
      <TicketActivationDetails bobcatData={ticket} />
      <FormElement
        label={formatMessage({ defaultMessage: 'Bärare:' })}
        input={<div className="text-text-900">{bearerContent}</div>}
      />
      {canChangeBearer && (
        <>
          <Button
            variant="outline"
            startIcon={<IconEdit />}
            className="ml-auto"
            onClick={() => setChangeBearerOpen(true)}
          >
            <FormattedMessage defaultMessage="Byt bärare" />
          </Button>
          <DialogWindow
            title={formatMessage({ defaultMessage: 'Byt bärare' })}
            content={
              <ChangeBearerDialog
                ticketId={ticket.id}
                travellerId={travellerId}
                bearerItems={bearerItems}
                mostRecentBobcatBearerId={mostRecentBobcatBearerId}
                isLoaned={!!ticket.loan_transaction_id}
                onOpenChange={setChangeBearerOpen}
                currentBearerId={currentBearer?.id}
              />
            }
            open={changeBearerOpen}
            onOpenChange={setChangeBearerOpen}
          />
        </>
      )}
    </li>
  );
};
